import { MoldOrderType, MoldOrderFirstLevelType, OrderDetail } from '../../types/mold';
import { BaseMoldOrderModel } from './base';

/**
 * Maintenance mold order model
 * Handles all maintenance-related order types and their specific logic
 */
export class MaintenanceMoldOrderModel extends BaseMoldOrderModel {
  readonly type: MoldOrderFirstLevelType = 'maintenance';
  readonly label = 'ล้างแม่พิมพ์';
  readonly description = 'ใบงานสำหรับการล้างและบำรุงรักษาแม่พิมพ์';
  readonly iconColor = 'text-blue-600';
  readonly headerBgColor = 'bg-gradient-to-br from-blue-50 to-blue-100/70';
  readonly headerBorderColor = 'border-blue-200/60';
  readonly headerShadow = 'shadow-lg shadow-blue-100/20';
  readonly headerTextColor = 'text-blue-900';

  getDefaultMoldOrderType(): MoldOrderType {
    return MoldOrderType.ALL_STORE;
  }

  getAvailableMoldOrderTypes(): MoldOrderType[] {
    return [
      MoldOrderType.ALL_STORE,
      MoldOrderType.ALL_PRODUCTION,
      MoldOrderType.WAITING,
      MoldOrderType.STORE_ORDER_COMPLETE,
      MoldOrderType.STORE_CLOSE_MOLD,
      MoldOrderType.STORE_EOL,
    ];
  }

  createInitialOrderDetail(): OrderDetail {
    return {
      dueDate: this.getDefaultDueDate(1),
      moldOrderType: this.getDefaultMoldOrderType(),
      note: '',
      form: {},
      shiftCompleted: this.getDefaultShiftType(),
      storageLocation: undefined,
      customStorageLocation: undefined,
    };
  }

  validateOrderDetail(orderDetail: OrderDetail): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Basic validation
    if (!orderDetail.dueDate) {
      errors.push('กรุณาระบุวันที่ครบกำหนด');
    }

    if (!orderDetail.moldOrderType) {
      errors.push('กรุณาเลือกประเภทงาน');
    }

    // Storage location validation
    if (this.requiresStorageLocation(orderDetail.moldOrderType)) {
      if (!orderDetail.storageLocation && !orderDetail.customStorageLocation) {
        errors.push('กรุณาระบุตำแหน่งที่เก็บ');
      }
    }

    // Shift completion validation
    if (this.requiresShiftCompletedInfo(orderDetail.moldOrderType)) {
      if (!orderDetail.shiftCompleted) {
        errors.push('กรุณาระบุกะที่ใช้งาน');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  getRequiredFormFields(): string[] {
    return ['moldOrderType', 'dueDate'];
  }

  requiresStorageLocation(moldOrderType: MoldOrderType): boolean {
    const storeTypes = [
      MoldOrderType.ALL_STORE,
      MoldOrderType.STORE_ORDER_COMPLETE,
      MoldOrderType.STORE_CLOSE_MOLD,
      MoldOrderType.STORE_EOL,
    ];
    return storeTypes.includes(moldOrderType);
  }

  requiresShiftCompletedInfo(moldOrderType: MoldOrderType): boolean {
    const productionTypes = [
      MoldOrderType.ALL_PRODUCTION,
      MoldOrderType.WAITING,
    ];
    return productionTypes.includes(moldOrderType);
  }

  requiresMachineToProduce(moldOrderType: MoldOrderType): boolean {
    const productionTypes = [
      MoldOrderType.ALL_PRODUCTION,
      MoldOrderType.WAITING,
    ];
    return productionTypes.includes(moldOrderType);
  }

  /**
   * Get specific default values based on maintenance order type
   */
  createInitialOrderDetailForType(moldOrderType: MoldOrderType): OrderDetail {
    const base = this.createInitialOrderDetail();
    
    return {
      ...base,
      moldOrderType,
      // Set default due date based on order type urgency
      dueDate: this.getDefaultDueDateForType(moldOrderType),
      // Set default shift based on order type
      shiftCompleted: this.getDefaultShiftForType(moldOrderType),
    };
  }

  private getDefaultDueDateForType(moldOrderType: MoldOrderType): Date {
    const urgentTypes = [MoldOrderType.ALL_PRODUCTION, MoldOrderType.WAITING];
    const offsetDays = urgentTypes.includes(moldOrderType) ? 0 : 1;
    return this.getDefaultDueDate(offsetDays);
  }

  private getDefaultShiftForType(moldOrderType: MoldOrderType): 'A' | 'B' {
    // Production orders might default to current shift
    const productionTypes = [MoldOrderType.ALL_PRODUCTION, MoldOrderType.WAITING];
    if (productionTypes.includes(moldOrderType)) {
      const currentHour = new Date().getHours();
      return currentHour < 20 ? 'A' : 'B';
    }
    return 'A';
  }

  /**
   * Get maintenance-specific validation rules
   */
  getMaintenanceValidationRules() {
    return {
      storageLocationRequired: (orderDetail: OrderDetail) => 
        this.requiresStorageLocation(orderDetail.moldOrderType),
      shiftRequired: (orderDetail: OrderDetail) => 
        this.requiresShiftCompletedInfo(orderDetail.moldOrderType),
      machineRequired: (orderDetail: OrderDetail) => 
        this.requiresMachineToProduce(orderDetail.moldOrderType),
    };
  }
}
